import type { SelectOption } from '@krp/ui-kit'

import type { OwnGroup } from '../api/contracts'

/**
 * Группы своей базы — дерево по `parentId`. Экрану нужно полное имя («Молочное / Сыры»), чтобы две «Сыры» в разных ветках
 * различались, и список родителей без самой группы и её потомков: служба такой перенос всё равно отвергнет.
 */
export interface GroupRow {
  id: string
  name: string
  path: string
  depth: number
  parentId: string | null
  archived: boolean
  version: number
}

const MAX_DEPTH = 64

export function groupPath(groups: ReadonlyMap<string, OwnGroup>, id: string): string {
  const names: string[] = []
  const seen = new Set<string>()
  for (let current = groups.get(id); current !== undefined && names.length < MAX_DEPTH; current = current.parentId === null ? undefined : groups.get(current.parentId)) {
    if (seen.has(current.id)) break
    seen.add(current.id)
    names.unshift(current.name)
  }
  return names.join(' / ')
}

/** Строки дерева в порядке обхода: родитель, затем его подгруппы по имени. Группа с потерянным родителем — в корне. */
export function groupRows(groups: readonly OwnGroup[]): readonly GroupRow[] {
  const byId = new Map(groups.map((group) => [group.id, group]))
  const children = new Map<string | null, OwnGroup[]>()
  for (const group of groups) {
    const parent = group.parentId !== null && byId.has(group.parentId) ? group.parentId : null
    children.set(parent, [...(children.get(parent) ?? []), group])
  }
  const rows: GroupRow[] = []
  const visit = (parent: string | null, depth: number) => {
    const level = [...(children.get(parent) ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    for (const group of level) {
      if (rows.some((row) => row.id === group.id)) continue
      rows.push({ id: group.id, name: group.name, path: groupPath(byId, group.id), depth, parentId: group.parentId, archived: group.archived, version: group.version })
      if (depth < MAX_DEPTH) visit(group.id, depth + 1)
    }
  }
  visit(null, 0)
  return rows
}

/** Сама группа и все её потомки — куда группу перенести нельзя. */
export function subtreeOf(groups: readonly OwnGroup[], id: string): ReadonlySet<string> {
  const result = new Set<string>([id])
  let grew = true
  while (grew) {
    grew = false
    for (const group of groups) {
      if (group.parentId !== null && result.has(group.parentId) && !result.has(group.id)) {
        result.add(group.id)
        grew = true
      }
    }
  }
  return result
}

/**
 * Варианты группы для поля: «без группы» первым, архивные — только если уже выбраны (в архивную группу товар не положить).
 * `exclude` — группы, которых в выборе быть не должно (сама переносимая группа с потомками).
 */
export function groupOptions(
  groups: readonly OwnGroup[],
  current: string,
  noneLabel: string,
  exclude: ReadonlySet<string> = new Set(),
): readonly SelectOption[] {
  const options: SelectOption[] = [{ value: '', label: noneLabel }]
  for (const row of groupRows(groups)) {
    if (exclude.has(row.id)) continue
    if (row.archived && row.id !== current) continue
    options.push({ value: row.id, label: row.archived ? `${row.path} (в архиве)` : row.path })
  }
  if (current !== '' && !options.some((option) => option.value === current)) options.push({ value: current, label: 'Группа не найдена' })
  return options
}
